import { BaseClient } from '../base-client';
import type {
  ReportColumnMeta,
  ReportEntityFilters,
  ReportListResponse,
  TimesheetReportExportQuery,
  TimesheetReportQuery,
} from './types';

/** Typed client for the Reports API (`/api/reports/*`). Gated server-side
 *  by `report.view`. */
export class ReportsClient extends BaseClient {
  /** Column metadata (key + header) for the Timesheet report preview table. */
  timesheetColumns(): Promise<{ data: ReportColumnMeta[] }> {
    return this.request('/api/reports/timesheets/columns');
  }

  /** A page of the Timesheet report for the preview table. */
  timesheetReport(query: TimesheetReportQuery): Promise<ReportListResponse> {
    return this.request('/api/reports/timesheets', { query: toQuery(query) });
  }

  /** Download the full Timesheet report (all matching rows) as an xlsx. */
  exportTimesheetReport(
    query: TimesheetReportExportQuery,
  ): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/reports/timesheets/export', {
      query: {
        fromDate: query.fromDate,
        toDate: query.toDate,
        status: joinStatus(query.status),
        ...entityQuery(query),
        includeMissing: includeMissingParam(query.includeMissing),
      },
    });
  }
}

/** The base client serializes only scalar query values, so the status
 *  array is joined into the comma-separated form the API splits back out. */
function toQuery(query: TimesheetReportQuery): Record<string, string | number | undefined> {
  return {
    fromDate: query.fromDate,
    toDate: query.toDate,
    status: joinStatus(query.status),
    ...entityQuery(query),
    includeMissing: includeMissingParam(query.includeMissing),
    limit: query.limit,
    offset: query.offset,
    sortField: query.sortField,
    sortDirection: query.sortDirection,
  };
}

function joinStatus(status: readonly string[] | undefined): string | undefined {
  return status && status.length > 0 ? status.join(',') : undefined;
}

/** Sent only when true (the API default is false), keeping the query string
 *  and the FE cache key free of no-op params. */
function includeMissingParam(value: boolean | undefined): string | undefined {
  return value ? 'true' : undefined;
}

const joinIds = joinStatus;

/** The five entity-id filters, each joined into the comma-separated form the
 *  API splits back out (same convention as `status`). */
function entityQuery(query: ReportEntityFilters): Record<string, string | undefined> {
  return {
    customerIds: joinIds(query.customerIds),
    employeeIds: joinIds(query.employeeIds),
    srControllerIds: joinIds(query.srControllerIds),
    teamLeadIds: joinIds(query.teamLeadIds),
    departmentIds: joinIds(query.departmentIds),
  };
}
